import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { Separator } from './ui/separator';
import { 
  Shield,
  Clock,
  Star,
  CheckCircle,
  AlertTriangle,
  FileText,
  Globe
} from 'lucide-react';

const EnhancedMembershipApplication = ({ contactForm, handleContactChange, handleContactSubmit, applicationState }) => {

  const isFormValid = () => {
    const cleanPhone = contactForm.phone ? contactForm.phone.replace(/\D/g, '') : '';
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const isEmailValid = contactForm.email && emailRegex.test(contactForm.email);
    
    // Phone validation - must be exactly 10 digits
    const isPhoneValid = cleanPhone.length === 10;
    
    // Basic validation
    const basicValid = contactForm.firstName && 
           contactForm.firstName.length >= 2 &&
           contactForm.lastName && 
           contactForm.lastName.length >= 2 &&
           isEmailValid &&
           isPhoneValid &&
           contactForm.membershipLevel &&
           contactForm.managementStatus &&
           contactForm.agreeToTerms && 
           contactForm.acknowledgedPMA;
    
    // Additional validation for contractors
    if (contactForm.managementStatus === 'Contractor') {
      return basicValid && contactForm.licenseStatus;
    }
    
    return basicValid;
  };

  const handleEnhancedSubmit = (e) => {
    e.preventDefault();
    if (!isFormValid()) {
      // Check specific validation issues
      const cleanPhone = contactForm.phone ? contactForm.phone.replace(/\D/g, '') : '';
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      
      let errorMessage = 'Please fix the following issues:\n';
      
      if (!contactForm.firstName || contactForm.firstName.length < 2) {
        errorMessage += '• First name must be at least 2 characters\n';
      }
      if (!contactForm.lastName || contactForm.lastName.length < 2) {
        errorMessage += '• Last name must be at least 2 characters\n';
      }
      if (!contactForm.email || !emailRegex.test(contactForm.email)) {
        errorMessage += '• Please enter a valid email address\n';
      }
      if (cleanPhone.length !== 10) {
        errorMessage += `• Phone number must be exactly 10 digits (currently ${cleanPhone.length})\n`;
      }
      if (!contactForm.membershipLevel) {
        errorMessage += '• Please select a membership level\n';
      }
      if (!contactForm.managementStatus) {
        errorMessage += '• Please select your property management status\n';
      }
      if (contactForm.managementStatus === 'Contractor' && !contactForm.licenseStatus) {
        errorMessage += '• Contractors must specify license status\n';
      }
      if (!contactForm.agreeToTerms || !contactForm.acknowledgedPMA) {
        errorMessage += '• Please agree to the terms and acknowledge the PMA\n';
      }
      
      alert(errorMessage);
      return;
    }
    
    // Store application data
    const applicationData = {
      ...contactForm,
      applicationTimestamp: new Date().toISOString(),
      status: 'pending_contract'
    };
    
    localStorage.setItem('legacyPropertyApplication', JSON.stringify(applicationData));
    
    // Workflow: Application → Contract Review → Portal Creation
    handleContactSubmit(e);
  };

  return (
    <section id="contact" className="py-20 bg-gradient-to-b from-slate-800 to-slate-900">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4 font-serif">
            Apply for Private Membership
          </h2>
          <p className="text-xl text-amber-100 max-w-3xl mx-auto">
            Complete your membership application and digitally sign your Private Member Association agreement. 
            Upon completion, you'll receive immediate portal access and membership activation.
          </p>
          
          <div className="flex flex-wrap justify-center gap-4 mt-8">
            <Badge className="bg-amber-500/20 text-amber-300 border-amber-400/30 backdrop-blur-sm px-4 py-2">
              <Shield className="w-4 h-4 mr-2" />
              Digital Contract Review & Signing
            </Badge>
            <Badge className="bg-amber-500/20 text-amber-300 border-amber-400/30 backdrop-blur-sm px-4 py-2">
              <Clock className="w-4 h-4 mr-2" />
              Immediate Portal Activation
            </Badge>
            <Badge className="bg-amber-500/20 text-amber-300 border-amber-400/30 backdrop-blur-sm px-4 py-2">
              <Star className="w-4 h-4 mr-2" />
              Illinois PMA Compliant
            </Badge>
          </div>
        </div>
        
        <div className="max-w-4xl mx-auto">
          <Card className="p-8 shadow-xl">
            <CardHeader className="px-0 pt-0">
              <CardTitle className="text-2xl flex items-center space-x-3">
                <FileText className="w-6 h-6 text-amber-600" />
                <span>Membership Application & Agreement</span>
              </CardTitle>
              <p className="text-slate-600">
                Complete your information below. By submitting, you agree to the Legacy Property Council PMA terms and will digitally sign the contract on the next page.
              </p>
            </CardHeader>
            
            <form onSubmit={handleEnhancedSubmit} className="space-y-8">
              {/* Basic Information */}
              <div>
                <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center space-x-2">
                  <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm">1</div>
                  <span>Personal Information</span>
                </h3>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      First Name *
                    </label>
                    <Input
                      required
                      value={contactForm.firstName}
                      onChange={(e) => handleContactChange('firstName', e.target.value)}
                      placeholder="Enter your first name"
                    />
                    <p className="text-xs text-slate-500 mt-1">Minimum 2 characters required</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Last Name *
                    </label>
                    <Input
                      required
                      value={contactForm.lastName}
                      onChange={(e) => handleContactChange('lastName', e.target.value)}
                      placeholder="Enter your last name"
                    />
                    <p className="text-xs text-slate-500 mt-1">Minimum 2 characters required</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Email Address *
                    </label>
                    <Input
                      type="email"
                      required
                      value={contactForm.email}
                      onChange={(e) => handleContactChange('email', e.target.value)}
                      placeholder="Enter your email address"
                      className={contactForm.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactForm.email) ? 'border-red-500' : ''}
                    />
                    {contactForm.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactForm.email) && (
                      <p className="text-xs text-red-500 mt-1">Please enter a valid email address</p>
                    )}
                    {contactForm.email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactForm.email) && (
                      <p className="text-xs text-green-600 mt-1">✓ Valid email address</p>
                    )}
                  </div>
                </div>
                
                <div className="mt-4">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Phone Number *
                  </label>
                  <Input
                    type="tel"
                    required
                    value={contactForm.phone}
                    onChange={(e) => handleContactChange('phone', e.target.value)}
                    placeholder="(555) 123-4567"
                    className={`max-w-md ${(() => {
                      const digits = contactForm.phone ? contactForm.phone.replace(/\D/g, '') : '';
                      if (digits.length > 0 && digits.length !== 10) return 'border-red-500';
                      if (digits.length === 10) return 'border-green-500';
                      return '';
                    })()}`}
                  />
                  {(() => {
                    const digits = contactForm.phone ? contactForm.phone.replace(/\D/g, '') : '';
                    if (digits.length === 0) {
                      return <p className="text-xs text-slate-500 mt-1">10-digit US phone number required</p>;
                    } else if (digits.length < 10) {
                      return <p className="text-xs text-red-500 mt-1">Phone number must be exactly 10 digits ({digits.length}/10)</p>;
                    } else if (digits.length === 10) {
                      return <p className="text-xs text-green-600 mt-1">✓ Valid phone number</p>;
                    }
                  })()}
                </div>
              </div>

              <Separator />

              {/* Membership Details */}
              <div>
                <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center space-x-2">
                  <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm">2</div>
                  <span>Membership Details</span>
                </h3>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Desired Membership Level *
                    </label>
                    <Select value={contactForm.membershipLevel} onValueChange={(value) => handleContactChange('membershipLevel', value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select membership level" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Basic">Basic - FREE (Educational Resources + Skills Network)</SelectItem>
                        <SelectItem value="Standard">Standard - $50/month (LPA Member Portal + All Basic Features)</SelectItem>
                        <SelectItem value="Premium">Premium - $100/month (Full Admin + Eviction Support + Land Trust Education + Reporting Tools)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Property Management Status *
                    </label>
                    <Select value={contactForm.managementStatus} onValueChange={(value) => handleContactChange('managementStatus', value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select current approach" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Self-managed">Self-managed properties</SelectItem>
                        <SelectItem value="Using another company">Using traditional management company</SelectItem>
                        <SelectItem value="Contractor">GC or Skilled Labor Network</SelectItem>
                        <SelectItem value="Hybrid approach">Hybrid self/professional approach</SelectItem>
                        <SelectItem value="New investor">New property owner</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Business Name - Available for all property management types */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Business Name
                      <span className="text-slate-500 text-xs ml-2">(Optional)</span>
                    </label>
                    <Input
                      value={contactForm.businessName || ''}
                      onChange={(e) => handleContactChange('businessName', e.target.value)}
                      placeholder="Enter your business or company name"
                    />
                  </div>
                  
                  {/* Contractor-specific fields - Only show when Contractor is selected */}
                  {contactForm.managementStatus === 'Contractor' && (
                    <>
                      {/* License Status */}
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          License Status
                          <span className="text-red-500 ml-1">*</span>
                        </label>
                        <Select value={contactForm.licenseStatus} onValueChange={(value) => handleContactChange('licenseStatus', value)}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select license status" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Licensed">Licensed</SelectItem>
                            <SelectItem value="Non-licensed">Non-licensed</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Specialty Skills */}
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          GC or Specialty Skills 
                          <span className="text-green-600 ml-1">⭐ NEW</span>
                          <span className="text-slate-500 text-xs ml-2">(Optional - Select all that apply)</span>
                        </label>
                        <div className="space-y-2">
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                            {[
                              'General Contractor', 
                              'Plumbing', 
                              'Electrical', 
                              'HVAC', 
                              'Lawn Care/Landscaping', 
                              'Snow Removal',
                              'Painting', 
                              'Flooring', 
                              'Roofing', 
                              'Carpentry',
                              'Cleaning Services',
                              'Property Inspection'
                            ].map(skill => (
                              <label key={skill} className="flex items-center space-x-2 text-sm">
                                <input
                                  type="checkbox"
                                  checked={contactForm.specialtySkills?.includes(skill) || false}
                                  onChange={(e) => {
                                    const currentSkills = contactForm.specialtySkills || [];
                                    let updatedSkills;
                                    if (e.target.checked) {
                                      updatedSkills = [...currentSkills, skill];
                                    } else {
                                      updatedSkills = currentSkills.filter(s => s !== skill);
                                    }
                                    handleContactChange('specialtySkills', updatedSkills);
                                  }}
                                  className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                                />
                                <span className="text-slate-700">{skill}</span>
                              </label>
                            ))}
                          </div>
                          <div className="mt-3">
                            <input
                              type="text"
                              placeholder="Other specialty (please specify)"
                              value={contactForm.otherSpecialty || ''}
                              onChange={(e) => handleContactChange('otherSpecialty', e.target.value)}
                              className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                            />
                          </div>
                          <p className="text-xs text-slate-500 mt-2">
                            💡 Select your skills to connect with other PMA members who may need your services or can provide referrals within our network.
                          </p>
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {/* Timeline and Primary Interest fields removed as requested */}
              </div>

              <Separator />

              {/* Portal Access Information */}
              <div>
                <div className="flex items-center space-x-2 mb-4">
                  <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-full flex items-center justify-center text-sm">3</div>
                  <h3 className="text-lg font-semibold text-slate-800">Portal Access Information</h3>
                </div>
                
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="flex items-start space-x-3">
                    <Globe className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-medium text-blue-800 mb-2">Landlord Portal Setup</h4>
                      <p className="text-sm text-blue-700 leading-relaxed">
                        After payment confirmation, you'll be redirected to create your username and password on the secure landlord portal. 
                        Portal access is available to Standard and Premium members for property management, tenant tracking, and administrative coordination.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <Separator />

              {/* PMA Agreement Terms */}
              <div>
                <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center space-x-2">
                  <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm">4</div>
                  <span>Private Member Association Agreement</span>
                </h3>
                
                <Card className="bg-slate-50 border-slate-200">
                  <CardContent className="p-6">
                    <h4 className="font-semibold text-slate-800 mb-3">Key Terms of Legacy Property Council PMA Agreement:</h4>
                    <div className="space-y-3 text-sm text-slate-700">
                      <div className="flex items-start space-x-2">
                        <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                        <span><strong>Private Association Status:</strong> This is a private membership association operating under private contract law, not public commerce or licensed property management.</span>
                      </div>
                      <div className="flex items-start space-x-2">
                        <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                        <span><strong>Member Services:</strong> Administrative coordination, educational resources, and member portal access provided exclusively to private members.</span>
                      </div>
                      <div className="flex items-start space-x-2">
                        <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                        <span><strong>Privacy Protection:</strong> Members maintain exclusive access to their property and tenant data. Legacy Property Council has administrative access for support purposes only.</span>
                      </div>
                      <div className="flex items-start space-x-2">
                        <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                        <span><strong>Dispute Resolution:</strong> All disputes resolved through private arbitration under Illinois law, not public courts, to the fullest extent permitted by law.</span>
                      </div>
                      <div className="flex items-start space-x-2">
                        <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                        <span><strong>Membership Obligations:</strong> Pay dues timely, use services in accordance with PMA guidelines, maintain confidentiality of member information.</span>
                      </div>
                      <div className="flex items-start space-x-2">
                        <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                        <span><strong>Termination:</strong> Either party may terminate membership with 30-day written notice. Portal access terminates immediately upon membership end.</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Agreement Checkboxes */}
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <Checkbox 
                    id="acknowledge-pma"
                    checked={contactForm.acknowledgedPMA || false}
                    onCheckedChange={(checked) => handleContactChange('acknowledgedPMA', checked)}
                    className="mt-1"
                  />
                  <label htmlFor="acknowledge-pma" className="text-sm text-slate-700 leading-relaxed">
                    <strong>I acknowledge and understand</strong> that Legacy Property Council is a Private Member Association operating under private contract law, not a licensed property management company in public commerce. I understand my rights and obligations as a private member. *
                  </label>
                </div>
                
                <div className="flex items-start space-x-3">
                  <Checkbox 
                    id="agree-terms"
                    checked={contactForm.agreeToTerms || false}
                    onCheckedChange={(checked) => handleContactChange('agreeToTerms', checked)}
                    className="mt-1"
                  />
                  <label htmlFor="agree-terms" className="text-sm text-slate-700 leading-relaxed">
                    <strong>I agree to be bound by the complete Legacy Property Council PMA Agreement</strong> which I will review and digitally sign on the next page. I consent to electronic signatures and understand this creates a legally binding contract under Illinois law. *
                  </label>
                </div>
              </div>

              {/* Additional Notes */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Additional Comments or Questions
                </label>
                <Textarea
                  value={contactForm.message}
                  onChange={(e) => handleContactChange('message', e.target.value)}
                  placeholder="Any specific questions about membership, portal access, or administrative services? (Optional)"
                  rows={3}
                />
              </div>
              
              {/* Error Message */}
              {applicationState?.error && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-start space-x-3">
                    <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-medium text-red-800">Application Error</h4>
                      <p className="text-sm text-red-700 mt-1">{applicationState.error}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Submit Button */}
              <div className="pt-6">
                <Button 
                  type="submit" 
                  disabled={!isFormValid() || applicationState?.isSubmitting}
                  className={`w-full py-4 text-lg font-semibold transition-all duration-300 ${
                    isFormValid() && !applicationState?.isSubmitting
                      ? 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white' 
                      : 'bg-slate-300 text-slate-500 cursor-not-allowed'
                  }`}
                  size="lg"
                >
                  {applicationState?.isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Submitting Application...
                    </>
                  ) : (
                    <>
                      <FileText className="w-5 h-5 mr-2" />
                      Submit Application & Review Contract
                    </>
                  )}
                </Button>
                
                <div className="mt-4 text-center">
                  <p className="text-xs text-slate-500 leading-relaxed">
                    Upon submission, you'll be taken to review and digitally sign your PMA contract. 
                    Contract completion activates your membership and portal access immediately.
                  </p>
                </div>
              </div>
            </form>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default EnhancedMembershipApplication;